import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import type { PlayerChapter } from './types'
import { usePlayerTone, type PlayerTone } from './tone'

/** 75 → "1:15"; 3725 → "1:02:05". */
export const formatClock = (value: number): string => {
  const total = Math.max(0, Math.floor(Number.isFinite(value) ? value : 0))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const seconds = String(total % 60).padStart(2, '0')
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${seconds}` : `${minutes}:${seconds}`
}

/** The chapter playing at `position` (the last one that started). */
export const chapterAt = (chapters: PlayerChapter[] | undefined, position: number): PlayerChapter | null => {
  let found: PlayerChapter | null = null
  for (const chapter of chapters || []) if (chapter.startSeconds <= position) found = chapter
  return found
}

interface ProgressBarProps {
  position: number
  duration: number
  chapters?: PlayerChapter[]
  onSeek: (seconds: number) => void
  /** `thin`: the mini-player's edge line (taller on hover). */
  variant?: 'full' | 'thin'
  /** On the cover's color (white bar) instead of the theme surface; default from `PlayerToneContext`. */
  tone?: PlayerTone
  className?: string
}

/**
 * The seek bar with the chapters marked on it (a gap at each start), Spotify
 * style. Drag or click to seek; arrows move 5 s, Page Up/Down 30 s.
 */
export function ProgressBar({ position, duration, chapters, onSeek, variant = 'full', tone: toneProp, className }: ProgressBarProps) {
  const { t } = useTranslation('player')
  const contextTone = usePlayerTone()
  const tone = toneProp ?? contextTone
  const trackRef = React.useRef<HTMLDivElement | null>(null)
  const [dragAt, setDragAt] = React.useState<number | null>(null)
  const [hoverAt, setHoverAt] = React.useState<number | null>(null)
  const total = duration > 0 ? duration : 0
  const shown = dragAt ?? position
  const percent = total ? Math.min(100, (shown / total) * 100) : 0
  const marks = total ? (chapters || []).filter((c) => c.startSeconds > 0 && c.startSeconds < total) : []
  const hoverChapter = hoverAt !== null ? chapterAt(chapters, hoverAt) : null

  const secondsAt = (clientX: number): number => {
    const rect = trackRef.current?.getBoundingClientRect()
    if (!rect || !total) return 0
    return Math.max(0, Math.min(total, ((clientX - rect.left) / rect.width) * total))
  }

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!total) return
    event.currentTarget.setPointerCapture(event.pointerId)
    setDragAt(secondsAt(event.clientX))
  }
  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!total) return
    const at = secondsAt(event.clientX)
    setHoverAt(at)
    if (dragAt !== null) setDragAt(at)
  }
  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragAt === null) return
    onSeek(secondsAt(event.clientX))
    setDragAt(null)
  }
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const steps: Record<string, number> = { ArrowLeft: -5, ArrowRight: 5, ArrowDown: -5, ArrowUp: 5, PageDown: -30, PageUp: 30 }
    if (event.key in steps) { event.preventDefault(); onSeek(position + steps[event.key]); return }
    if (event.key === 'Home') { event.preventDefault(); onSeek(0) }
    if (event.key === 'End' && total) { event.preventDefault(); onSeek(total - 1) }
  }

  const thin = variant === 'thin'
  const onColor = tone === 'onColor'
  const active = dragAt !== null
  return (
    <div
      ref={trackRef}
      role="slider"
      tabIndex={0}
      aria-label={t('seek')}
      aria-valuemin={0}
      aria-valuemax={Math.round(total)}
      aria-valuenow={Math.round(shown)}
      aria-valuetext={t('positionOf', { position: formatClock(shown), duration: formatClock(total) })}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={() => setHoverAt(null)}
      onKeyDown={onKeyDown}
      className={cn(
        'group/bar relative flex cursor-pointer touch-none select-none items-center rounded-full outline-none focus-visible:ring-2',
        onColor ? 'focus-visible:ring-[rgb(var(--pl-on-hero))]' : 'focus-visible:ring-[rgb(var(--pl-accent))]',
        thin ? 'h-3' : 'h-5',
        className
      )}
    >
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-full transition-[height] duration-150 motion-reduce:transition-none',
          onColor ? 'bg-[rgb(var(--pl-on-hero)/0.30)]' : 'bg-muted-foreground/25',
          thin ? 'h-1 group-hover/bar:h-1.5 group-focus-visible/bar:h-1.5' : 'h-1 group-hover/bar:h-1.5 group-focus-visible/bar:h-1.5',
          active && 'h-1.5'
        )}
      >
        <div className={cn('absolute inset-y-0 left-0 rounded-full', onColor ? 'bg-[rgb(var(--pl-on-hero))]' : 'bg-[rgb(var(--pl-accent))]')} style={{ width: `${percent}%` }} />
        {marks.map((chapter) => (
          <span
            key={`${chapter.startSeconds}-${chapter.title}`}
            aria-hidden
            className={cn('absolute inset-y-0 w-[3px] -translate-x-1/2', onColor ? 'bg-[rgb(var(--pl-hero))]' : 'bg-surface')}
            style={{ left: `${(chapter.startSeconds / total) * 100}%` }}
          />
        ))}
      </div>
      {total > 0 && (
        <span
          aria-hidden
          className={cn(
            'absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-md ring-2 transition-opacity motion-reduce:transition-none',
            thin ? 'h-3 w-3' : 'h-3.5 w-3.5',
            onColor ? 'bg-[rgb(var(--pl-on-hero))] ring-[rgb(var(--pl-hero)/0.3)]' : 'bg-[rgb(var(--pl-accent))] ring-surface',
            !active && 'opacity-0 group-hover/bar:opacity-100 group-focus-visible/bar:opacity-100'
          )}
          style={{ left: `${percent}%` }}
        />
      )}
      {hoverAt !== null && total > 0 && (
        <span
          aria-hidden
          className="pointer-events-none absolute bottom-full z-10 mb-1.5 max-w-[16rem] -translate-x-1/2 truncate whitespace-nowrap rounded-md bg-surface-inverse px-2 py-0.5 text-[11px] font-medium tabular-nums text-white shadow theme-light"
          style={{ left: `${Math.min(92, Math.max(8, (hoverAt / total) * 100))}%` }}
        >
          {formatClock(hoverAt)}{hoverChapter ? ` · ${hoverChapter.title}` : ''}
        </span>
      )}
    </div>
  )
}
